import axios from "axios";
import { convertPdfToImages, isPdf, isImage } from "../utils/pdfToImages";

const API_BASE_URL = "http://localhost:4000";
// Self-hosted n8n instance
const N8N_WEBHOOK_URL = "https://n8n.infrasync.tech/webhook/start-analysis";

const api = axios.create({
  timeout: 300000, // 5 minutes for processing
});

interface ProcessingCallback {
  (progress: number, step: string): void;
}

interface AnalysisRequest {
  workflowId: string;
  documents: string[];
  damageImages: string[];
  documentsContent?: { filename: string; content: string; type: string }[];
  damageImagesContent?: { filename: string; content: string; type: string }[];
  requestTramer?: boolean;
  vehiclePlate?: string;
  vehicleInfo?: any;
  location?: string;
  additionalNotes?: string;
  additionalInfo: {
    vehicleInfo?: any;
    location?: string;
    notes?: string;
    testMode?: boolean;
    timestamp: string;
  };
}

// Helper function to convert file to base64
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data:type;base64, prefix
      const base64Data = base64.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
}

export async function processInsuranceClaim(
  formData: FormData,
  onProgress?: ProcessingCallback
): Promise<any> {
  try {
    // Extract files and data from FormData
    const documents: string[] = [];
    const damageImages: string[] = [];
    const documentsContent: {
      filename: string;
      content: string;
      type: string;
    }[] = [];
    const damageImagesContent: {
      filename: string;
      content: string;
      type: string;
    }[] = [];
    const documentFiles: File[] = [];
    const damageImageFiles: File[] = [];
    let vehicleInfo = "";
    let location = "";
    let additionalNotes = "";
    let requestTramer = false;
    let vehiclePlate = "";

    // Process FormData entries
    for (const [key, value] of formData.entries()) {
      if (key === "documents" && value instanceof File) {
        documents.push(value.name);
        documentFiles.push(value);
      } else if (key === "damageImages" && value instanceof File) {
        damageImages.push(value.name);
        damageImageFiles.push(value);
      } else if (key === "vehicleInfo") {
        vehicleInfo = value as string;
      } else if (key === "location") {
        location = value as string;
      } else if (key === "additionalNotes") {
        additionalNotes = value as string;
      } else if (key === "requestTramer") {
        requestTramer = value === "true";
      } else if (key === "vehiclePlate") {
        vehiclePlate = value as string;
      }
    }

    if (onProgress) {
      onProgress(5, "Dosyalar okunuyor...");
    }

    // Process document files - convert PDFs to images
    for (const file of documentFiles) {
      try {
        if (isPdf(file)) {
          // Convert PDF pages to images
          if (onProgress) {
            onProgress(10, `PDF dönüştürülüyor: ${file.name}...`);
          }
          console.log(`📄 Converting PDF to images: ${file.name}`);
          
          const pdfResult = await convertPdfToImages(file, 2.0);
          
          // Add each page as a separate image
          for (const page of pdfResult.pages) {
            documentsContent.push({
              filename: `${file.name}_page${page.pageNumber}.jpg`,
              content: page.base64,
              type: page.mimeType,
            });
          }
          
          console.log(`✅ Converted ${pdfResult.totalPages} pages from ${file.name}`);
        } else if (isImage(file)) {
          // Regular image - just convert to base64
          const content = await fileToBase64(file);
          documentsContent.push({
            filename: file.name,
            content,
            type: file.type,
          });
        } else {
          // Try to read as base64 anyway
          const content = await fileToBase64(file);
          documentsContent.push({
            filename: file.name,
            content,
            type: file.type || 'application/octet-stream',
          });
        }
      } catch (error) {
        console.warn(`Failed to process file ${file.name}:`, error);
      }
    }

    // Process damage image files - convert PDFs to images
    for (const file of damageImageFiles) {
      try {
        if (isPdf(file)) {
          // Convert PDF pages to images
          if (onProgress) {
            onProgress(15, `PDF dönüştürülüyor: ${file.name}...`);
          }
          console.log(`📄 Converting PDF to images: ${file.name}`);
          
          const pdfResult = await convertPdfToImages(file, 2.0);
          
          // Add each page as a separate image
          for (const page of pdfResult.pages) {
            damageImagesContent.push({
              filename: `${file.name}_page${page.pageNumber}.jpg`,
              content: page.base64,
              type: page.mimeType,
            });
          }
          
          console.log(`✅ Converted ${pdfResult.totalPages} pages from ${file.name}`);
        } else {
          // Regular image - just convert to base64
          const content = await fileToBase64(file);
          damageImagesContent.push({
            filename: file.name,
            content,
            type: file.type,
          });
        }
      } catch (error) {
        console.warn(`Failed to process file ${file.name}:`, error);
      }
    }

    console.log("📁 Documents to process:", documents);
    console.log("📷 Damage images to process:", damageImages);
    console.log("💾 Documents with content (after PDF conversion):", documentsContent.length);
    console.log("💾 Damage images with content (after PDF conversion):", damageImagesContent.length);

    // Simulate progress updates for better UX
    const progressSteps = [
      { progress: 20, step: "AI analizi başlatılıyor..." },
      { progress: 35, step: "Claude 3.5 ile belgeler işleniyor..." },
      { progress: 50, step: "Görseller analiz ediliyor..." },
      { progress: 65, step: "Kusur dağılımı değerlendiriliyor..." },
      { progress: 80, step: "Hasar miktarı hesaplanıyor..." },
      { progress: 95, step: "Uzman raporu oluşturuluyor..." },
    ];

    let currentStepIndex = 0;
    const progressInterval = setInterval(() => {
      if (currentStepIndex < progressSteps.length && onProgress) {
        const step = progressSteps[currentStepIndex];
        onProgress(step.progress, step.step);
        currentStepIndex++;
      }
    }, 3000);

    // Parse vehicle info
    let parsedVehicleInfo = undefined;
    try {
      parsedVehicleInfo = vehicleInfo ? JSON.parse(vehicleInfo) : undefined;
    } catch (e) {
      console.warn("Failed to parse vehicleInfo:", e);
    }

    // Prepare request for n8n webhook
    const analysisRequest: AnalysisRequest = {
      workflowId: `wf-${Date.now()}`,
      documents,
      damageImages,
      documentsContent,
      damageImagesContent,
      requestTramer,
      vehiclePlate,
      vehicleInfo: parsedVehicleInfo,
      location,
      additionalNotes,
      additionalInfo: {
        vehicleInfo: parsedVehicleInfo,
        location,
        notes: additionalNotes,
        testMode: false,
        timestamp: new Date().toISOString(),
      },
    };

    console.log("🚀 Sending to n8n workflow:", {
      ...analysisRequest,
      documentsContent: `[${documentsContent.length} files]`,
      damageImagesContent: `[${damageImagesContent.length} files]`,
    });

    const response = await axios.post(N8N_WEBHOOK_URL, analysisRequest, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 300000, // 5 minute timeout
    });

    clearInterval(progressInterval);

    // Handle n8n response
    if (onProgress) {
      onProgress(100, "Analiz tamamlandı!");
    }

    console.log("✅ n8n workflow response:", response.data);

    // Handle empty response from n8n
    if (
      !response.data ||
      (typeof response.data === "object" &&
        Object.keys(response.data).length === 0)
    ) {
      return {
        status: "error",
        error: "n8n workflow boş yanıt döndürdü. Lütfen workflow yapılandırmasını kontrol edin.",
        message: "Analiz isteği alındı ancak sonuç döndürülmedi.",
        timestamp: new Date().toISOString(),
      };
    }

    return response.data;
  } catch (error: any) {
    console.error("❌ Analysis failed:", error);

    // Provide user-friendly error message
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Analiz beklenmedik şekilde başarısız oldu";

    throw new Error(`Analiz başarısız: ${errorMessage}`);
  }
}

// New function for direct n8n webhook calls
export async function callN8nWorkflow(data: AnalysisRequest): Promise<any> {
  try {
    console.log("🔗 Calling n8n workflow directly:", data);

    const response = await api.post(N8N_WEBHOOK_URL, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("✅ n8n workflow response:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      console.error("❌ n8n workflow error:", errorMessage);
      throw new Error(`Workflow failed: ${errorMessage}`);
    }
    throw error;
  }
}

// Keep existing export functions but mark as deprecated
export async function exportToMarkdown(
  reportData: any,
  templateType: "full" | "summary" = "full"
) {
  console.warn(
    "⚠️ exportToMarkdown is deprecated - reports are now generated by n8n workflow"
  );
  return reportData;
}

export async function exportToPDF(
  reportData: any,
  templateType: "full" | "summary" = "full",
  pdfOptions?: any
) {
  console.warn(
    "⚠️ exportToPDF is deprecated - PDF generation is handled by n8n workflow"
  );
  return reportData;
}

export async function downloadFile(filename: string) {
  console.warn("⚠️ downloadFile function needs to be updated for n8n workflow");
  throw new Error("File download not yet implemented for n8n workflow");
}

// Check n8n health status
export async function getHealthStatus() {
  try {
    const response = await axios.get("http://localhost:5679/webhook/health", {
      timeout: 5000,
    });
    return { status: "healthy", service: "n8n" };
  } catch (error) {
    console.error("❌ n8n health check failed:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return { status: "unhealthy", service: "n8n", error: errorMessage };
  }
}
